import { escapeHtml } from "../core/html.js";
import { MAINTENANCE_CONTEXT_DAYS, maintenanceContextEvents, selectMaintenanceContext } from "../core/maintenance-context.js";
import type { MaintenanceContextSelection, MaintenanceContextEvent } from "../core/maintenance-context.js";

type Deps = {
  enabled: () => boolean;
  aircraftId: (aircraft: string) => string | null;
  openMaintenance: (aircraft: string) => void;
  onRender?: () => void;
};

export function createMaintenanceContext({ enabled, aircraftId, openMaintenance, onRender = () => {} }: Deps) {
  const requests = new Map<string, Promise<MaintenanceContextEvent[]>>();
  const mounts = new Map<string, { container: HTMLElement; selection: MaintenanceContextSelection; token: object; key: string }>();

  async function load(id: string) {
    if (requests.has(id)) return requests.get(id)!;
    const promise = (async () => {
      const [maintenance, borescope] = await Promise.all(["maintenance", "borescope"].map(async path => {
        const response = await fetch(`/api/aircraft/${encodeURIComponent(id)}/${path}`, { cache: "no-store" });
        if (!response.ok) throw new Error("Maintenance context unavailable");
        return response.json();
      }));
      return maintenanceContextEvents(maintenance, borescope);
    })();
    requests.set(id, promise);
    try { return await promise; }
    finally { if (requests.get(id) === promise) requests.delete(id); }
  }

  function clear(container: HTMLElement) { container.hidden = true; container.replaceChildren(); }

  async function render(view: string, container: HTMLElement | null, selection: MaintenanceContextSelection) {
    if (!container) { mounts.delete(view); return; }
    const key = JSON.stringify(selection);
    const previous = mounts.get(view);
    const token = {};
    mounts.set(view, { container, selection, token, key });
    const id = aircraftId(selection.aircraft);
    if (!enabled() || !id || !selection.anchors.length) { clear(container); return; }
    if (previous?.container !== container || previous?.key !== key) clear(container);
    try {
      const events = await load(id);
      if (mounts.get(view)?.token !== token || !container.isConnected || aircraftId(selection.aircraft) !== id) return;
      if (!enabled()) { clear(container); return; }
      const result = selectMaintenanceContext(events, selection);
      if (!result.records.length) { clear(container); onRender(); return; }
      const rows = result.records.map(record => {
        const relation = record.days === 0 ? "Same recorded day as" : `${Math.abs(record.days)} day${Math.abs(record.days) === 1 ? "" : "s"} ${record.days < 0 ? "before" : "after"}`;
        const scope = record.cylinders?.length ? ` · Cyl ${record.cylinders.join(", ")}` : "";
        return `<li><strong>${escapeHtml(record.title)}</strong><span>${escapeHtml(record.day)} · ${escapeHtml(record.engineLabel)}${escapeHtml(scope)}</span>
          <small>${relation} ${escapeHtml(record.anchor.label.toLowerCase())} (${escapeHtml(record.anchor.day)})</small></li>`;
      }).join("");
      container.innerHTML = `<section class="maintenance-review-context" aria-label="Maintenance context">
        <h4>Maintenance context</h4><p>Records within ${MAINTENANCE_CONTEXT_DAYS} days of the ${result.anchors.some(anchor => anchor.label === "Reference flight") ? "latest or reference flight dates" : `${escapeHtml(result.anchors[0].label.toLowerCase())} date`}. Dates provide context and do not establish a cause.</p>
        <ul>${rows}</ul>${result.total > result.records.length ? `<p>Showing ${result.records.length} nearest records of ${result.total} in this window.</p>` : ""}
        <button class="secondary-action" type="button" data-open-maintenance>Open Maintenance</button></section>`;
      container.hidden = false;
      container.querySelector("button")?.addEventListener("click", () => { if (enabled()) openMaintenance(selection.aircraft); });
      onRender();
    } catch {
      if (mounts.get(view)?.token === token) { clear(container); onRender(); }
    }
  }

  function refresh(id?: string) {
    if (id) requests.delete(id); else requests.clear();
    for (const [view, mount] of mounts) {
      if (!mount.container.isConnected) { mounts.delete(view); continue; }
      if (!id || aircraftId(mount.selection.aircraft) === id) void render(view, mount.container, mount.selection);
    }
  }

  return { render, refresh };
}
